import React, { useState } from 'react';
import { MessageSquarePlus, AlertTriangle, User } from 'lucide-react';
import { STATUS_META, STATUS_ORDER, PRIORITY_META } from '@/constants/projectMeta';

export const ProjectKanbanView = ({ tasks, canManage, meId, onEdit, onStatusChange, onAddUpdate, today }) => {
  const [dragId, setDragId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const drop = (status) => {
    const task = tasks.find((t) => t.id === dragId);
    setDragId(null);
    setHoverCol(null);
    if (task && task.status !== status) onStatusChange(task, status);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="project-kanban">
      {STATUS_ORDER.map((status) => {
        const meta = STATUS_META[status];
        const cols = tasks.filter((t) => t.status === status);
        return (
          <div
            key={status}
            onDragOver={(e) => { e.preventDefault(); setHoverCol(status); }}
            onDragLeave={() => setHoverCol((c) => (c === status ? null : c))}
            onDrop={() => drop(status)}
            className={`rounded-xl border p-2.5 min-h-[220px] transition-colors ${hoverCol === status
              ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
              : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))]'}`}
            data-testid={`kanban-column-${status}`}
          >
            <div className="flex items-center justify-between mb-2.5 px-1">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: meta.accent }} />
                <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-primary))]">{meta.label}</span>
              </div>
              <span className="text-[11px] text-[hsl(var(--text-muted))]" data-testid={`kanban-count-${status}`}>{cols.length}</span>
            </div>

            <div className="space-y-2">
              {cols.map((t) => {
                const overdue = t.deadline && t.deadline < today && !['completed', 'cancelled'].includes(t.status);
                const isMine = t.pic_id === meId || (t.collaborators || []).some((c) => c.id === meId);
                return (
                  <div
                    key={t.id}
                    draggable={canManage || isMine}
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    className={`rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] p-2.5 cursor-pointer hover:-translate-y-0.5 hover:border-[hsl(var(--primary))]/50 transition-all ${dragId === t.id ? 'opacity-50' : ''}`}
                    onClick={() => onAddUpdate(t)}
                    data-testid={`kanban-card-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-medium text-[hsl(var(--text-primary))] leading-snug">{t.title}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAddUpdate(t); }}
                        className="shrink-0 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))]"
                        data-testid={`kanban-update-btn-${t.id}`}
                      >
                        <MessageSquarePlus size={13} />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-[hsl(var(--text-muted))]">
                      <span className="inline-flex items-center gap-1"><User size={10} />{t.pic_name || '—'}</span>
                      <span className={(PRIORITY_META[t.priority] || PRIORITY_META.medium).text}>
                        {(PRIORITY_META[t.priority] || PRIORITY_META.medium).label}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {t.department && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[hsl(var(--text-muted))]">{t.department}</span>
                      )}
                      {t.project && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">{t.project}</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${t.progress || 0}%`, background: meta.accent }} />
                      </div>
                      <span className="text-[10px] text-[hsl(var(--text-muted))]">{t.progress || 0}%</span>
                    </div>
                    {t.deadline && (
                      <div className={`mt-1.5 text-[10px] ${overdue ? 'text-red-400' : 'text-[hsl(var(--text-muted))]'}`}>
                        {overdue && <AlertTriangle size={10} className="inline mr-1" />}{t.deadline}
                      </div>
                    )}
                  </div>
                );
              })}
              {!cols.length && (
                <div className="text-[11px] text-[hsl(var(--text-muted))] text-center py-6">Empty</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
